<template>
  <div id="accordionSupplierNotificationLog" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingNotificationLog" class="accordion-header">
        <button
          aria-controls="collapseNotificationLog"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseNotificationLog"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
              <v-icon icon="description" size="xs" class="text-white" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.notificationLog") }}</div>
              <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("clientTour.notificationLogSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseNotificationLog"
        aria-labelledby="headingNotificationLog"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionSupplierNotificationLog"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <div class="notification-log">
            <template v-if="!!supplierNotificationLogs && supplierNotificationLogs.length > 0">
              <div
                v-for="(notification, index) in supplierNotificationLogs"
                :key="notification.id"
                class="notification-log-item"
                :class="{ 'is-last': index === supplierNotificationLogs.length - 1 }"
              >
                <div class="notification-log-dot">
                  <v-icon icon="mail" size="xs" class="text-white" />
                </div>

                <div class="notification-log-content">
                  <div class="d-flex align-items-center justify-content-between flex-wrap gap-4">
                    <span class="fw-bold text-sm">{{ formatEmailType(notification.email_type) }}</span>
                    <span class="badge bg-success-soft text-success text-xs">Sent</span>
                  </div>

                  <div v-if="!!notification.supplier" class="text-muted text-xs mt-4">
                    <v-icon icon="business" size="xs" space="me-4" />
                    {{ notification.supplier.name }}
                  </div>

                  <div class="text-muted text-xs mt-4">
                    <v-icon icon="person" size="xs" space="me-4" />
                    <template v-if="notification.sent_by">{{ notification.sent_by.name }}</template>
                    <span class="text-muted-subtle"> via </span>
                    <span class="fw-medium">{{ notification.email_from || 'booking@waw.travel' }}</span>
                    <span class="text-muted-subtle"> sent to </span>
                    <span class="fw-medium">{{ notification.email_to || '—' }}</span>
                  </div>

                  <div class="text-muted-subtle text-xs mt-4">
                    <v-icon icon="schedule" size="xs" space="me-4" />
                    {{ $str.formatDateTime(notification.created_at, settings.formatDatetime) }}
                  </div>
                </div>
              </div>
            </template>

            <div v-else class="text-center text-muted italic py-24">
              {{ $t("clientTour.noSuppliersNotifications") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import { useAppStore } from "@/stores/app";
import { findConceptByKey } from "@/helpers/ConceptHelper";

const props = defineProps<{
  agency: string;
  tour: number;
  clientTour: number;
  supplierNotificationLogs:{
    type: any[],
    required: true,
    default: []
  }
}>();

const appStore = useAppStore();
const settings = appStore.settings;

const formatEmailType = (emailType: string): string => {
  if (!emailType) return '—';
  const concept = findConceptByKey('supplier.email_notification_types', emailType);
  if (concept && typeof concept !== 'string') {
    return concept.name || emailType;
  }
  return emailType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

.notification-log {
  max-height: 23rem;
  overflow-y: auto;
  padding-right: 4px;
}

.notification-log-item {
  display: flex;
  gap: 12px;
  padding-bottom: 16px;
  position: relative;

  &:not(.is-last)::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 28px;
    bottom: 0;
    width: 2px;
    background-color: var(--uq-border-color, #e0e0e0);
  }
}

.notification-log-dot {
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--uq-primary, #0d6efd);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.notification-log-content {
  flex: 1;
  min-width: 0;
}

.text-muted-subtle {
  color: var(--uq-text-muted, #9e9e9e);
}

.bg-success-soft {
  background-color: rgba(25, 135, 84, 0.12);
}
</style>
